/**
 * Medicine alarm sound.
 *
 * Ported from the `window.capsuleAlarm` object in alarm.js. The DOM modal it
 * built by hand is now a React component (`MedicineAlarmModal`); what remains
 * here is the audio, which genuinely belongs outside React because it is a
 * long-lived browser resource.
 *
 * Two sound paths, as before: a synthesised four-tone beep via Web Audio, or a
 * custom ringtone the patient uploaded (stored as a data URI on their user row).
 */

const BEEP_PATTERN = [
  { offset: 0.0, frequency: 880, duration: 0.25 },
  { offset: 0.35, frequency: 1100, duration: 0.25 },
  { offset: 0.7, frequency: 880, duration: 0.25 },
  { offset: 1.05, frequency: 1100, duration: 0.5 },
];

const BEEP_LOOP_MS = 2000;

class AlarmService {
  constructor() {
    this.audioContext = null;
    this.loopTimer = null;
    this.htmlAudio = null;
    this.customRingtoneUrl = null;
    this.playing = false;
  }

  /** Lazily create the AudioContext — constructing one before a user gesture is blocked. */
  ensureContext() {
    if (!this.audioContext) {
      const Ctor = window.AudioContext || window.webkitAudioContext;
      if (!Ctor) return null;
      this.audioContext = new Ctor();
    }
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume().catch(() => {});
    }
    return this.audioContext;
  }

  /** One pass of the default beep pattern. */
  playBeep() {
    const ctx = this.ensureContext();
    if (!ctx) return;

    const start = ctx.currentTime;
    for (const { offset, frequency, duration } of BEEP_PATTERN) {
      const at = start + offset;
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();

      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;

      gain.gain.setValueAtTime(0, at);
      gain.gain.linearRampToValueAtTime(0.6, at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, at + duration);

      oscillator.start(at);
      oscillator.stop(at + duration + 0.05);
    }
  }

  /** Set (or clear, with `null`) the patient's uploaded ringtone. */
  setCustomRingtone(dataUri) {
    this.customRingtoneUrl = dataUri || null;
  }

  /** Begin looping until `stop()` is called. */
  start() {
    this.stop();
    this.playing = true;

    if (this.customRingtoneUrl) {
      if (!this.htmlAudio) this.htmlAudio = new Audio();
      this.htmlAudio.src = this.customRingtoneUrl;
      this.htmlAudio.loop = true;
      this.htmlAudio.volume = 1;
      this.htmlAudio.play().catch((err) => {
        // Autoplay policy blocked it — fall back to the synthesised beep, which
        // works because the AudioContext was unlocked by an earlier interaction.
        console.warn('[Alarm] Custom ringtone blocked, using the default beep:', err.message);
        this.playBeep();
        this.loopTimer = setInterval(() => this.playBeep(), BEEP_LOOP_MS);
      });
      return;
    }

    this.playBeep();
    this.loopTimer = setInterval(() => this.playBeep(), BEEP_LOOP_MS);
  }

  stop() {
    this.playing = false;
    if (this.loopTimer) {
      clearInterval(this.loopTimer);
      this.loopTimer = null;
    }
    if (this.htmlAudio) {
      this.htmlAudio.pause();
      this.htmlAudio.currentTime = 0;
    }
  }

  isPlaying() {
    return this.playing;
  }

  /**
   * Play one short beep as a preview — used by the ringtone picker so the
   * patient can hear a choice without arming a full alarm.
   */
  preview() {
    if (this.customRingtoneUrl) {
      const audio = new Audio(this.customRingtoneUrl);
      audio.volume = 1;
      audio.play().catch(() => this.playBeep());
      setTimeout(() => audio.pause(), 3000);
      return;
    }
    this.playBeep();
  }

  /** Release everything; called when the app unmounts or the user signs out. */
  dispose() {
    this.stop();
    if (this.audioContext) {
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }
    this.htmlAudio = null;
  }
}

export const alarmService = new AlarmService();
export default alarmService;
